import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCallback } from 'react';
import { useTranslation } from '@/i18n';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { smoothScrollTo } from './scrollUtils';

export function LandingFooter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const scrollTo = useCallback((id: string) => {
    if (location.pathname !== '/') {
      void navigate(`/#${id}`);
      return;
    }
    smoothScrollTo(id);
  }, [location.pathname, navigate]);

  return (
    <footer className="bg-gray-900 text-gray-400 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img src="/logo.svg" alt={t('common.brand.name')} className="h-8 w-8" />
              <span className="text-lg font-bold text-white">
                {t('common.brand.name')}
              </span>
            </div>
            <p className="text-sm leading-relaxed">
              {t('landing.footer.tagline')}
            </p>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">
              {t('landing.footer.product')}
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <button
                  onClick={() => scrollTo('how-it-works')}
                  className="hover:text-white transition-colors cursor-pointer"
                >
                  {t('landing.nav.howItWorks')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollTo('features')}
                  className="hover:text-white transition-colors cursor-pointer"
                >
                  {t('landing.nav.features')}
                </button>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">
              {t('landing.footer.company')}
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <span className="text-gray-500 cursor-default">
                  {t('landing.footer.about')}
                </span>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="hover:text-white transition-colors"
                >
                  {t('landing.footer.contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">
              {t('landing.footer.legal')}
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <span className="text-gray-500 cursor-default">
                  {t('landing.footer.terms')}
                </span>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="hover:text-white transition-colors"
                >
                  {t('landing.footer.privacy')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm">
            {t('landing.footer.copyright')}
          </p>
          <LanguageSwitcher />
        </div>
      </div>
    </footer>
  );
}
